import { OpsTatStatus } from "../OpsTat/OpsTatFilters";

export type DailyTasksTatStatus = OpsTatStatus;

export type DailyTasksTatMetricId =
  | "cycle_total"
  | "sla_adherence"
  | "top_drag"
  | "custom_mix"
  | "created_to_logo"
  | "created_to_upload"
  | "created_to_last_upload"
  | "upload_to_approval"
  | "approved_to_first_file"
  | "approved_to_last_file"
  | "first_file_to_printer"
  | "file_to_printer"
  | "approved_to_sent"
  | "handoff_to_first_pr"
  | "handoff_to_covering_pr"
  | "pr_to_sourcing"
  | "pr_to_finance"
  | "finance_to_po"
  | "po_to_vendor"
  | "po_to_raw_in"
  | "raw_in_to_raw_qc"
  | "raw_qc_to_print_out"
  | "print_out_to_print_in"
  | "print_in_to_print_qc"
  | "request_to_project"
  | "request_to_finance"
  | "created_to_variants"
  | "created_to_invoice"
  | "activity_logos"
  | "activity_mockups"
  | "activity_products"
  | "activity_rework"
  | "activity_rejected"
  | "activity_approved"
  | "activity_deleted"
  | "activity_files"
  | "activity_file_products"
  | "activity_sent_to_printer"
  | "activity_prs"
  | "activity_pr_products"
  | "activity_covering_prs"
  | "activity_pos"
  | "activity_po_products"
  | "activity_all_pos"
  | "activity_po_emails"
  | "activity_po_cancelled"
  | "activity_raw_grns"
  | "activity_raw_full"
  | "activity_raw_qc"
  | "activity_raw_rejects"
  | "activity_print_sends"
  | "activity_print_full"
  | "activity_print_qty"
  | "activity_print_inwards"
  | "activity_print_in_full"
  | "activity_print_in_qc"
  | "activity_print_in_rejects"
  | "activity_project_requests"
  | "activity_project_finance"
  | "activity_project_variants"
  | "activity_project_invoices";

export type DailyTasksTatGrain = "order_product" | "order";

export type DailyTasksTatRecordScope = "measured" | "open" | "all";

export type DailyTasksTatSortDir = "asc" | "desc";

export interface DailyTasksTatPercentiles {
  mean: number;
  p50: number;
  p90: number;
}

export interface DailyTasksTatMetaRange {
  start: string | null;
  end: string | null;
  label: string | null;
}

export interface DailyTasksTatMeta {
  range: DailyTasksTatMetaRange;
  generated_at: string;
  path: "custom";
  filters_applied: number;
  include_running: boolean;
  metricId?: string;
}

export interface DailyTasksTatSort {
  column: string;
  dir: DailyTasksTatSortDir;
}

export interface DailyTasksTatTopDrag {
  stageId: string;
  name: string;
  delta: number;
  share_of_overrun_pct: number;
}

export interface DailyTasksTatHeadline {
  path: "custom";
  unit: "days";
  cycle: DailyTasksTatPercentiles | null;
  target: number;
  delta: number | null;
  status: DailyTasksTatStatus;
  orders: number;
  measured: number;
  in_flight: number;
  sla_adherence_pct: number | null;
  breached: number | null;
  top_drag: DailyTasksTatTopDrag | null;
  custom_mix_pct: number | null;
}

export interface DailyTasksTatStageCard {
  stageId: string;
  code: string;
  name: string;
  short: string;
  owner: string;
  unit: "days";
  actual: DailyTasksTatPercentiles | null;
  target: number;
  delta: number | null;
  status: DailyTasksTatStatus;
  n: number;
  share_of_overrun_pct: number;
  trend: Array<number | null>;
  grain: DailyTasksTatGrain;
  applies_to: "custom";
  ready: boolean;
  size_driven: boolean;
  open?: number;
  per_unit?: {
    basis: number;
    days_per_basis: DailyTasksTatPercentiles;
  };
}

export interface DailyTasksTatActivity {
  logos_uploaded: number;
  mockups_uploaded: number;
  mockups_approved: number;
  changes_requested: number;
  mockups_rejected: number;
  mockups_deleted: number;
  products_touched: number;
  uploads_per_product: number | null;
  files_uploaded: number;
  file_products_touched: number;
  files_sent_to_printer: number;
  prs_created: number;
  pr_products_touched: number;
  covering_prs: number;
  pos_created: number;
  po_products_touched: number;
  all_pos: number;
  emails_sent: number;
  pos_cancelled: number;
  raw_grns: number;
  raw_full_inward: number;
  raw_qc_closed: number;
  raw_qty_rejected: number;
  print_sends: number;
  print_full: number;
  print_qty: number;
  print_inwards: number;
  print_in_full: number;
  print_in_qc_closed: number;
  print_in_qty_rejected: number;
  project_requests: number;
  project_finance_decisions: number;
  project_variants_covered: number;
  project_invoices: number;
}

export const DAILY_TASKS_ACTIVITY_METRIC: Record<
  keyof DailyTasksTatActivity,
  DailyTasksTatMetricId
> = {
  logos_uploaded: "activity_logos",
  mockups_uploaded: "activity_mockups",
  products_touched: "activity_products",
  uploads_per_product: "activity_products",
  changes_requested: "activity_rework",
  mockups_rejected: "activity_rejected",
  mockups_approved: "activity_approved",
  mockups_deleted: "activity_deleted",
  files_uploaded: "activity_files",
  file_products_touched: "activity_file_products",
  files_sent_to_printer: "activity_sent_to_printer",
  prs_created: "activity_prs",
  pr_products_touched: "activity_pr_products",
  covering_prs: "activity_covering_prs",
  pos_created: "activity_pos",
  po_products_touched: "activity_po_products",
  all_pos: "activity_all_pos",
  emails_sent: "activity_po_emails",
  pos_cancelled: "activity_po_cancelled",
  raw_grns: "activity_raw_grns",
  raw_full_inward: "activity_raw_full",
  raw_qc_closed: "activity_raw_qc",
  raw_qty_rejected: "activity_raw_rejects",
  print_sends: "activity_print_sends",
  print_full: "activity_print_full",
  print_qty: "activity_print_qty",
  print_inwards: "activity_print_inwards",
  project_requests: "activity_project_requests",
  project_finance_decisions: "activity_project_finance",
  project_variants_covered: "activity_project_variants",
  project_invoices: "activity_project_invoices",
  print_in_full: "activity_print_in_full",
  print_in_qc_closed: "activity_print_in_qc",
  print_in_qty_rejected: "activity_print_in_rejects",
};

export const isDailyTasksActivityMetric = (metricId: string): boolean =>
  metricId.startsWith("activity_");

export const DAILY_TASKS_ACTIVITY_DRAWER_COPY: Record<
  string,
  { desc: string; note: string }
> = {
  activity_logos: {
    desc: "Each row is one client logo uploaded in the selected dates, either on a product or on the whole order.",
    note: "Counted when the logo was uploaded, not when the order was created. Click a product row for details.",
  },
  activity_mockups: {
    desc: "Each row is one mockup file uploaded in the selected dates.",
    note: "Counted when the file was uploaded, not when the order was created. Click a row for details.",
  },
  activity_products: {
    desc: "Each row is a product that received at least one mockup file in the selected dates. Files is how many were uploaded; First upload is the earliest in this window.",
    note: "Uploads / product on the strip is mockups uploaded ÷ these products. Click a row for details.",
  },
  activity_deleted: {
    desc: "Each row is a soft-deleted mockup file (active = 0) whose modified_date falls in the selected dates.",
    note: "Counted on modified_date when the file was deleted. Older deletes may be missing if modified_date was not set at delete time.",
  },
  activity_rework: {
    desc: "Each row is a mockup that had changes requested in the selected dates.",
    note: "Counted on the rework date, not when the order was created. Click a row for details.",
  },
  activity_rejected: {
    desc: "Each row is a mockup that was rejected in the selected dates.",
    note: "Counted on the rejection date, not when the order was created. Click a row for details.",
  },
  activity_approved: {
    desc: "Each row is a mockup file that was approved in the selected dates.",
    note: "Counted on the approval date, not when the order was created. Click a row for details.",
  },
  activity_files: {
    desc: "Each row is one printing file uploaded in the selected dates.",
    note: "Counted when the file was uploaded, not when the order was created. Click a row for details.",
  },
  activity_file_products: {
    desc: "Each row is a product that received at least one printing file in the selected dates.",
    note: "Counted when the first file in this window was uploaded. Click a row for details.",
  },
  activity_sent_to_printer: {
    desc: "Each row is a product marked sent to printer in the selected dates.",
    note: "Counted when the product was marked sent to printer, not when the order was created. Click a row for details.",
  },
  activity_prs: {
    desc: "Each row is one purchase request created in the selected dates (not rejected or cancelled).",
    note: "Counted when the purchase request was created, not when the order was created. Click a row for details.",
  },
  activity_pr_products: {
    desc: "Each row is a product that received at least one purchase request in the selected dates.",
    note: "Counted when the first purchase request in this window was created. Click a row for details.",
  },
  activity_covering_prs: {
    desc: "Each row is a product whose clean PR quantity first covered the ordered quantity in the selected dates (same covering rule as Ops TAT).",
    note: "Counted on the full-qty date, not when the order was created. Click a row for details.",
  },
  activity_pos: {
    desc: "Each row is one issued purchase order created in the selected dates.",
    note: "Counted when the purchase order was created, not when the order was created. Click a row for details.",
  },
  activity_po_products: {
    desc: "Each row is a product that received at least one issued purchase order in the selected dates.",
    note: "Counted when the first purchase order in this window was created. Click a row for details.",
  },
  activity_all_pos: {
    desc: "Each row is a product whose last missing purchase order was issued in the selected dates.",
    note: "Counted when every approved PR on the product first had a PO, not when the order was created. Click a row for details.",
  },
  activity_po_emails: {
    desc: "Each row is one purchase order whose vendor email was sent in the selected dates.",
    note: "Counted when the vendor email was sent, not when the order was created. Click a row for details.",
  },
  activity_po_cancelled: {
    desc: "Each row is one purchase order cancelled (status CANCELLED) whose modified_date falls in the selected dates.",
    note: "Counted on modified_date when the PO was cancelled. There is no separate cancelled_at column.",
  },
  activity_raw_grns: {
    desc: "Each row is one inward logged in the selected dates.",
    note: "Counted when the inward was created, not when the order was created. Click a row for details.",
  },
  activity_raw_full: {
    desc: "Each row is a product that first reached full inward quantity in the selected dates.",
    note: "Counted when received qty first covered the ordered qty, not when the order was created.",
  },
  activity_raw_qc: {
    desc: "Each row is a product whose QC was closed in the selected dates.",
    note: "Counted when QC end_time was set, not when the order was created.",
  },
  activity_raw_rejects: {
    desc: "Each row is rejected quantity recorded in the selected dates.",
    note: "Counted on the QC row, not when the order was created.",
  },
  activity_print_sends: {
    desc: "Each row is a print outward with quantity greater than zero logged in the selected dates.",
    note: "Counted when the outward was created, not when the order was created. 0-qty stubs are excluded.",
  },
  activity_print_full: {
    desc: "Each row is a product whose MAX outward quantity first covered the ordered quantity in the selected dates.",
    note: "Counted when outward qty first covered ordered qty, not when the order was created.",
  },
  activity_print_qty: {
    desc: "Each row is a print outward batch with quantity outward in the selected dates.",
    note: "Files column shows the incremental quantity_sent for that batch.",
  },
  activity_print_inwards: {
    desc: "Each row is a print inward with received quantity greater than zero logged in the selected dates.",
    note: "Counted when the inward was created, not when the order was created. 0-qty stubs are excluded.",
  },
  activity_print_in_full: {
    desc: "Each row is a product whose MAX received quantity first covered the ordered quantity in the selected dates.",
    note: "Counted when received qty first covered ordered qty, not when the order was created.",
  },
  activity_print_in_qc: {
    desc: "Each row is a product whose print QC was closed in the selected dates.",
    note: "Counted when print QC end_time was set, not when the order was created.",
  },
  activity_print_in_rejects: {
    desc: "Each row is rejected quantity recorded on print QC in the selected dates.",
    note: "Counted on the print QC row, not when the order was created.",
  },
  activity_project_requests: {
    desc: "Each row is a project request created in the selected dates for a cohort order.",
    note: "Counted when the request was raised, not when the order was created.",
  },
  activity_project_finance: {
    desc: "Each row is a project request whose finance approval or rejection landed in the selected dates.",
    note: "Counted on the finance decision date, not when the order was created.",
  },
  activity_project_variants: {
    desc: "Each row is a product whose active variants quantity first covered ordered qty in the selected dates.",
    note: "MVP covering_at is MAX(ODV.created_at) when covered. Counted when covering was reached, not when the order was created.",
  },
  activity_project_invoices: {
    desc: "Each row is the first active customer invoice for an order whose invoice date falls in the selected dates.",
    note: "Customer invoice from invoice.invoices (active=1), not vendor GRN invoice_received.",
  },
};

export interface DailyTasksTatSummaryData {
  headline: DailyTasksTatHeadline;
  stages: DailyTasksTatStageCard[];
  activity: DailyTasksTatActivity;
}

export interface DailyTasksTatSummaryResult {
  meta: DailyTasksTatMeta;
  data: DailyTasksTatSummaryData;
}

export interface DailyTasksTatDrilldownQuery {
  page: number;
  page_size: number;
  record_scope: DailyTasksTatRecordScope;
  sort?: DailyTasksTatSort;
}

export const DAILY_TASKS_TAT_DEFAULT_PAGE_SIZE = 100;

export const defaultDailyTasksTatDrilldownQuery =
  (): DailyTasksTatDrilldownQuery => ({
    page: 1,
    page_size: DAILY_TASKS_TAT_DEFAULT_PAGE_SIZE,
    record_scope: "measured",
  });

export interface DailyTasksTatStat {
  label: string;
  value: number | null;
  unit: string;
  color?: "clay";
}

export interface DailyTasksTatDistributionBucket {
  lo: number;
  hi: number | null;
  count: number;
}

export interface DailyTasksTatDistribution {
  unit: "days";
  buckets: DailyTasksTatDistributionBucket[];
  p50: number | null;
  p90: number | null;
}

export interface DailyTasksTatTableColumn {
  key: string;
  label: string;
  type: "text" | "number" | "datetime" | "date" | "pill";
  unit?: string;
}

export interface DailyTasksTatTable {
  columns: DailyTasksTatTableColumn[];
  rows: Array<Record<string, unknown>>;
  page: number;
  page_size: number;
  total: number;
  sort: DailyTasksTatSort;
}

export interface DailyTasksTatDrilldownData {
  metricId: string;
  grain: DailyTasksTatGrain;
  kicker: string;
  title: string;
  desc: string;
  unit: string;
  stats: DailyTasksTatStat[];
  formula: string;
  lineage: string[];
  distribution: DailyTasksTatDistribution | null;
  table: DailyTasksTatTable;
  note: string;
  ready: boolean;
}

export interface DailyTasksTatDrilldownResult {
  meta: DailyTasksTatMeta;
  data: DailyTasksTatDrilldownData;
}

export interface DailyTasksTatAnalyticsBody {
  start_date: string;
  end_date: string;
  search?: string;
  order_type_id?: number[];
  order_id?: number[];
  product_id?: number[];
  customer_id?: number[];
  pm_poc_id?: number[];
  sales_poc_id?: number[];
  warehouse_id?: string;
  excluded_orders?: boolean;
  /** null = both; true = archived; false = active. */
  project_status?: boolean | null;
  include_running?: boolean;
  stage?: string;
  date_field?: string;
  page?: number;
  page_size?: number;
  sort?: DailyTasksTatSort;
  record_scope?: DailyTasksTatRecordScope;
  format?: "json" | "csv";
}
